import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Badge, Loading } from '../ui/Page';
import {
  previewEmailTemplate,
  resetEmailTemplate,
  saveEmailTemplate,
  type EmailTemplateView,
  type TemplatePreview,
} from './api';

/**
 * A tenant's own wording for a platform e-mail (Phase 133).
 *
 * The API has been able to do this since Phase 16 — an override is a row of this tenant's, resolved
 * over the platform text at send time — and no screen ever reached it, so the list said "Padrão da
 * plataforma" on every row with no way to make it say anything else.
 *
 * Three things this screen has to be careful about, none of them cosmetic:
 *
 * **The placeholders are a contract, not decoration.** `{{tenantName}}` is filled by the code that
 * sends the message; a typo does not fail, it delivers "Você foi convidado para {{tenantNme}}" to a
 * customer. They are listed, they are one click to insert, and the preview names the ones that came
 * out empty.
 *
 * **Reset is a real action.** Once a tenant overrides a template they stop receiving improvements
 * to the platform wording for it — including corrections. Going back has to be one button, and it
 * deletes the row rather than copying the default text back in, so the next improvement arrives.
 *
 * **It never edits another tenant's anything.** The whole thing runs on the tenant connection
 * behind `email:manage`; this file only has to avoid pretending otherwise on screen.
 */

export function TemplateEditor({
  template,
  onClose,
}: {
  template: EmailTemplateView;
  onClose: () => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const [name, setName] = useState(template.name);
  const [subject, setSubject] = useState(template.subject);
  const [bodyText, setBodyText] = useState(template.bodyText);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [mode, setMode] = useState<'html' | 'text'>('html');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Reopening on a different row must not keep the previous row's text in the boxes.
  useEffect(() => {
    setName(template.name);
    setSubject(template.subject);
    setBodyText(template.bodyText);
    setPreview(null);
    setSaved(false);
    setError(null);
  }, [template]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['email-templates'] });
  };

  const save = useMutation({
    mutationFn: () =>
      saveEmailTemplate(template.key, {
        name: name.trim(),
        subject: subject.trim(),
        bodyText,
        bodyHtml: template.bodyHtml ?? null,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const reset = useMutation({
    mutationFn: () => resetEmailTemplate(template.key),
    onSuccess: () => {
      refresh();
      onClose();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const show = useMutation({
    /*
     * Sample values, named after the placeholder itself.
     *
     * The alternative is asking somebody to invent a value for every variable before they can see
     * anything, and the question the preview answers is "does my wording read right", not "what
     * does it say for Ada". A placeholder the template does not fill still shows up in `missing`.
     */
    mutationFn: () =>
      previewEmailTemplate(
        template.key,
        Object.fromEntries(
          (template.variables ?? []).map((variable) => [variable, `[${variable}]`]),
        ),
      ),
    onSuccess: (result) => {
      setError(null);
      setPreview(result);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const insert = (variable: string): void => {
    setBodyText((current) => `${current}{{${variable}}}`);
    setSaved(false);
  };

  return (
    <aside className="drawer" role="dialog" aria-label={t('email.editTemplate')}>
      <div className="drawer__head">
        <span className="cell-stack">
          <strong>{template.name}</strong>
          <span className="muted small">
            <code>{template.key}</code>
          </span>
        </span>

        <button type="button" className="button button--quiet button--compact" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      <div className="drawer__body stack">
        <div className="cell-inline">
          {template.isOverride ? (
            <Badge tone="warning">{t('email.customised')}</Badge>
          ) : (
            <Badge tone="neutral">{t('email.platformDefault')}</Badge>
          )}
          <span className="muted small">{t('email.editHint')}</span>
        </div>

        <label className="stack__field">
          <span>{t('email.templateName')}</span>
          <input
            className="input"
            value={name}
            maxLength={200}
            onChange={(event) => {
              setName(event.target.value);
              setSaved(false);
            }}
          />
        </label>

        <label className="stack__field">
          <span>{t('email.subject')}</span>
          <input
            className="input"
            value={subject}
            maxLength={300}
            onChange={(event) => {
              setSubject(event.target.value);
              setSaved(false);
            }}
          />
        </label>

        <label className="stack__field">
          <span>{t('email.body')}</span>
          <textarea
            className="input template-editor__body"
            rows={14}
            value={bodyText}
            onChange={(event) => {
              setBodyText(event.target.value);
              setSaved(false);
            }}
          />
        </label>

        {(template.variables?.length ?? 0) > 0 && (
          <div className="stack__field">
            <span className="muted small">{t('email.variablesHint')}</span>
            <div className="chips">
              {template.variables!.map((variable) => (
                <button
                  key={variable}
                  type="button"
                  className="chip"
                  onClick={() => insert(variable)}
                >
                  {`{{${variable}}}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        {saved && <p className="muted small">{t('email.templateSaved')}</p>}

        <div className="cell-inline">
          <button
            type="button"
            className="button button--primary"
            disabled={save.isPending || !subject.trim() || !bodyText.trim() || !name.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.saving') : t('common.save')}
          </button>

          <button
            type="button"
            className="button button--quiet"
            disabled={show.isPending}
            onClick={() => show.mutate()}
          >
            {t('email.preview')}
          </button>

          {/* Only offered when there is something of this tenant's to drop. */}
          {template.isOverride && (
            <button
              type="button"
              className="button button--quiet"
              disabled={reset.isPending}
              onClick={() => reset.mutate()}
            >
              {t('email.resetTemplate')}
            </button>
          )}
        </div>

        {show.isPending && <Loading />}

        {preview && (
          <div className="template-editor__preview">
            <p className="strong">{preview.subject}</p>

            {/*
              Two tabs, because they are two different questions.

              "Does my wording read right" is answered by the text; "what will this look like in
              their inbox" is answered by the rendered message, in the company's own colours and
              with its logo. Showing only the text was the old answer to both.
            */}
            <div className="segmented" role="tablist" aria-label={t('email.preview')}>
              <button
                type="button"
                role="tab"
                className="segmented__option"
                aria-selected={mode === 'html'}
                onClick={() => setMode('html')}
              >
                {t('email.previewHtml')}
              </button>
              <button
                type="button"
                role="tab"
                className="segmented__option"
                aria-selected={mode === 'text'}
                onClick={() => setMode('text')}
              >
                {t('email.previewText')}
              </button>
            </div>

            {mode === 'html' && preview.html ? (
              /*
                A sandboxed frame, and `srcDoc` rather than a URL.

                The template is written by somebody in this tenant holding `email:manage`, but it is
                still content: rendering it into this page would let a template's markup reach the
                application's own DOM. `sandbox` with nothing enabled means no scripts, no forms and
                no navigation — it draws, and that is all it can do.
              */
              <iframe
                title={t('email.previewHtml')}
                className="template-editor__frame"
                sandbox=""
                srcDoc={preview.html}
              />
            ) : (
              <pre className="template-editor__rendered">{preview.text}</pre>
            )}

            {preview.missing.length > 0 && (
              <p className="muted small">
                {t('email.previewMissing').replace('{list}', preview.missing.join(', '))}
              </p>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
